import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Search, Ticket, AlertTriangle, Clock, ChevronRight, Building2, Crown, Star, ArrowUpDown, ArrowUp, ArrowDown, User
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const TicketsList = () => {
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Default to 'active' (excludes closed)
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' or 'mine'
  const [sortField, setSortField] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc'); // newest first

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await api.get('/api/super-admin/tickets');
        setTickets(res.data);
      } catch (err) {
        console.error('Failed to fetch tickets:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [api]);

  const filteredTickets = tickets.filter(t => {
    const matchesSearch =
      t.subject?.toLowerCase().includes(search.toLowerCase()) ||
      t.ticket_number?.toLowerCase().includes(search.toLowerCase()) ||
      t.company_name?.toLowerCase().includes(search.toLowerCase());
    
    // Status filter - 'active' hides closed tickets
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = t.status !== 'closed';
    } else if (statusFilter !== 'all') {
      matchesStatus = t.status === statusFilter;
    }
    
    const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    const matchesTier = tierFilter === 'all' || t.tenant_tier === tierFilter;
    
    // Assignee filter
    let matchesAssignee = true;
    if (assigneeFilter === 'mine') {
      matchesAssignee = t.assigned_to_id === admin?.id;
    } else if (assigneeFilter === 'unassigned') {
      matchesAssignee = !t.assigned_to_id;
    }
    
    return matchesSearch && matchesStatus && matchesPriority && matchesTier && matchesAssignee;
  });

  // Sort tickets
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    let aValue, bValue;
    
    if (sortField === 'updated_at') {
      aValue = new Date(a.updated_at || a.created_at);
      bValue = new Date(b.updated_at || b.created_at);
    } else if (sortField === 'created_at') {
      aValue = new Date(a.created_at);
      bValue = new Date(b.created_at);
    } else {
      return 0;
    }
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-400';
      case 'open': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-400';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      case 'closed': return 'bg-slate-500/20 text-slate-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-red-400" />;
      case 'high': return <Clock className="h-4 w-4 text-orange-400" />;
      case 'medium': return <Clock className="h-4 w-4 text-yellow-400" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'professional':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            <Crown className="h-3 w-3 mr-1" />
            Pro
          </Badge>
        );
      case 'advanced':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Star className="h-3 w-3 mr-1" />
            Adv
          </Badge>
        );
      default:
        return null; // No badge for basic tier
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  const openCount = tickets.filter(t => ['open', 'in_progress', 'waiting_on_customer'].includes(t.status)).length;
  const closedCount = tickets.filter(t => t.status === 'closed').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Tickets</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {openCount} open tickets
            {closedCount > 0 && <span className="ml-2">• {closedCount} closed (hidden)</span>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              <Input
                placeholder="Search by subject, ticket # or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`w-full sm:w-44 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active (excl. Closed)</SelectItem>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                <SelectItem value="waiting_on_support">Waiting on Support</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className={`w-full sm:w-36 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className={`w-full sm:w-36 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
              </SelectContent>
            </Select>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className={`w-full sm:w-40 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                <SelectValue placeholder="Assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tickets</SelectItem>
                <SelectItem value="mine">Assigned to Me</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          {sortedTickets.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className={`h-12 w-12 mx-auto ${isDark ? 'text-slate-500' : 'text-gray-400'} mb-4`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No tickets found</p>
              {statusFilter === 'active' && closedCount > 0 && (
                <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {closedCount} closed ticket{closedCount !== 1 ? 's' : ''} hidden.{' '}
                  <button 
                    onClick={() => setStatusFilter('all')} 
                    className="text-[#ffd108] hover:underline"
                  >
                    Show all
                  </button>
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Subject</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Company</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Assignee</TableHead>
                  <TableHead 
                    className={`${isDark ? 'text-slate-400' : 'text-gray-500'} cursor-pointer hover:text-[#ffd108] select-none`}
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Created
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={`${isDark ? 'text-slate-400' : 'text-gray-500'} cursor-pointer hover:text-[#ffd108] select-none`}
                    onClick={() => toggleSort('updated_at')}
                  >
                    <div className="flex items-center">
                      Last Updated
                      {getSortIcon('updated_at')}
                    </div>
                  </TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className={`${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                    onClick={() => navigate(`/super-admin/tickets/${ticket.id}`)}
                  >
                    <TableCell className="text-[#ffd108] font-mono text-sm">
                      {ticket.ticket_number}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'} capitalize`}>{ticket.category}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                        <span className={isDark ? 'text-slate-300' : 'text-gray-600'}>{ticket.company_name}</span>
                        {getTierBadge(ticket.tenant_tier)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(ticket.status)}>
                        {ticket.status?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getPriorityIcon(ticket.priority)}
                        <span className={`${isDark ? 'text-slate-300' : 'text-gray-600'} capitalize`}>{ticket.priority}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {ticket.assigned_to_name ? (
                        <div className="flex items-center gap-2">
                          <User className={`h-4 w-4 ${ticket.assigned_to_id === admin?.id ? 'text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                          <span className={ticket.assigned_to_id === admin?.id ? 'text-[#ffd108] font-medium' : isDark ? 'text-slate-300' : 'text-gray-600'}>
                            {ticket.assigned_to_id === admin?.id ? 'Me' : ticket.assigned_to_name}
                          </span>
                        </div>
                      ) : (
                        <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {ticket.updated_at 
                        ? formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketsList;
